import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf, Users, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, IMAGES, fadeInUp, staggerContainer, scaleIn } from '../config';

const valueIcons = [
  <Heart key="0" size={40} />,
  <Leaf key="1" size={40} />,
  <Users key="2" size={40} />,
  <Sparkles key="3" size={40} />,
];

const ChiSiamo = () => {
  const { t } = useLanguage();
  const valuesItems = t('chiSiamoPage.values.items');

  return (
    <>
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.restaurant})` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(114, 47, 55, 0.9) 0%, rgba(30, 58, 95, 0.85) 100%)` }} />
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-4"
          >
            {t('chiSiamoPage.hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl italic"
            style={{ color: COLORS.gold }}
          >
            {t('chiSiamoPage.hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.p
                variants={fadeInUp}
                className="text-xs tracking-[0.2em] font-semibold uppercase mb-4"
                style={{ color: COLORS.redwine }}
              >
                {t('concept.overline')}
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-8"
                style={{ color: COLORS.redwine }}
              >
                {t('concept.title')}
              </motion.h2>
              <motion.div variants={fadeInUp} className="w-16 h-1 mb-8" style={{ backgroundColor: COLORS.gold }} />
              
              <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-6" style={{ color: COLORS.nero }}>
                {t('concept.text1')}
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-6" style={{ color: COLORS.nero }}>
                {t('concept.text2')}
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg leading-relaxed" style={{ color: COLORS.nero }}>
                {t('concept.text3')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={IMAGES.buffet}
                alt="Buffet"
                className="rounded-xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div 
                className="absolute -bottom-8 -left-8 p-6 rounded-xl max-w-xs"
                style={{ backgroundColor: COLORS.navy }}
              >
                <p className="font-['Cormorant_Garamond'] text-lg italic" style={{ color: COLORS.vaniglia }}>
                  "{t('concept.highlight')}"
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24" style={{ backgroundColor: COLORS.redwine }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4"
              style={{ color: COLORS.vaniglia }}
            >
              {t('chiSiamoPage.values.title')}
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.gold }} />
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {Array.isArray(valuesItems) && valuesItems.map((value, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center p-8 rounded-xl"
                style={{ backgroundColor: `${COLORS.vaniglia}10`, border: `1px solid ${COLORS.vaniglia}20` }}
              >
                <div className="mb-4" style={{ color: COLORS.gold }}>{valueIcons[index]}</div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl mb-3" style={{ color: COLORS.vaniglia }}>
                  {value.title}
                </h3>
                <p className="text-sm" style={{ color: `${COLORS.vaniglia}b3` }}>{value.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Vision */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={IMAGES.pasta}
                alt="Vision"
                className="rounded-xl w-full h-64 object-cover mb-8"
              />
              <motion.p
                variants={fadeInUp}
                className="text-xs tracking-[0.2em] font-semibold uppercase mb-4"
                style={{ color: COLORS.gold }}
              >
                {t('vision.overline')}
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="font-['Cormorant_Garamond'] text-3xl md:text-4xl mb-6"
                style={{ color: COLORS.redwine }}
              >
                {t('vision.title')}
              </motion.h2>
              <motion.div variants={fadeInUp} className="w-12 h-1 mb-6" style={{ backgroundColor: COLORS.gold }} />
              <motion.p
                variants={fadeInUp}
                className="leading-relaxed"
                style={{ color: `${COLORS.nero}cc` }}
              >
                {t('vision.text')}
              </motion.p>
            </motion.div>

            {/* Mission */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img
                src={IMAGES.food}
                alt="Mission"
                className="rounded-xl w-full h-64 object-cover mb-8"
              />
              <motion.p
                variants={fadeInUp}
                className="text-xs tracking-[0.2em] font-semibold uppercase mb-4"
                style={{ color: COLORS.sage }}
              >
                {t('mission.overline')}
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="font-['Cormorant_Garamond'] text-3xl md:text-4xl mb-6"
                style={{ color: COLORS.redwine }}
              >
                {t('mission.title')}
              </motion.h2>
              <motion.div variants={fadeInUp} className="w-12 h-1 mb-6" style={{ backgroundColor: COLORS.sage }} />
              <motion.p
                variants={fadeInUp}
                className="leading-relaxed mb-6"
                style={{ color: `${COLORS.nero}cc` }}
              >
                {t('mission.text1')} {t('mission.text2')} {t('mission.text3')}
              </motion.p>
              <motion.div 
                variants={scaleIn}
                className="p-6 rounded-lg"
                style={{ backgroundColor: COLORS.navy }}
              >
                <p className="font-['Cormorant_Garamond'] text-lg italic" style={{ color: COLORS.sage }}>
                  "{t('mission.highlight')}"
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ChiSiamo;
